import { useMemo } from 'react';
import * as THREE from 'three';

// Individual plane component
export const GridPlane = ({
  origin,
  primaryAxis,
  secondaryAxis,
  primarySize,
  secondarySize,
  primaryDivisionUnit,
  secondaryDivisionUnit,
  color,
  opacity,
  children,
}: {
  origin: { x: number; y: number; z: number };
  primaryAxis: 'x' | 'y' | 'z';
  secondaryAxis: 'x' | 'y' | 'z';
  primarySize: number;
  secondarySize: number;
  primaryDivisionUnit: number;
  secondaryDivisionUnit: number;
  color: string;
  opacity: number;
  children?: React.ReactNode;
}) => {
  const lines = useMemo(() => {
    const lineArray: THREE.Vector3[] = [];
    const primaryLineCount = Math.floor(primarySize / primaryDivisionUnit);
    const secondaryLineCount = Math.floor(
      secondarySize / secondaryDivisionUnit
    );

    // Lines parallel to primaryAxis at different secondaryAxis positions
    for (let i = 0; i <= secondaryLineCount; i++) {
      const coords1 = { ...origin };
      const coords2 = { ...origin };

      coords1[secondaryAxis] += i * secondaryDivisionUnit;
      coords2[secondaryAxis] += i * secondaryDivisionUnit;
      coords2[primaryAxis] += primarySize;

      lineArray.push(
        new THREE.Vector3(coords1.x, coords1.y, coords1.z),
        new THREE.Vector3(coords2.x, coords2.y, coords2.z)
      );
    }

    // Lines parallel to secondaryAxis at different primaryAxis positions
    for (let i = 0; i <= primaryLineCount; i++) {
      const coords1 = { ...origin };
      const coords2 = { ...origin };

      coords1[primaryAxis] += i * primaryDivisionUnit;
      coords2[primaryAxis] += i * primaryDivisionUnit;
      coords2[secondaryAxis] += secondarySize;

      lineArray.push(
        new THREE.Vector3(coords1.x, coords1.y, coords1.z),
        new THREE.Vector3(coords2.x, coords2.y, coords2.z)
      );
    }

    return lineArray;
  }, [
    origin,
    primaryAxis,
    secondaryAxis,
    primarySize,
    secondarySize,
    primaryDivisionUnit,
    secondaryDivisionUnit,
  ]);

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setFromPoints(lines);
    return geo;
  }, [lines]);

  const material = useMemo(() => {
    return new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
    });
  }, [color, opacity]);

  return (
    <group>
      <lineSegments geometry={geometry} material={material} />
      {children}
    </group>
  );
};
